import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { DashboardCard } from "@pages:app/analytics/components/cards/DashboardCard";
import { useTranslation } from "react-i18next";

export type DonutSlice = {
  id: string;
  label: string;
  value: number;
  color: string;
};

const EMPTY_COLOR = "#EEEDF3";

type DonutCardProps = {
  title: string;
  slices: DonutSlice[];
  centerValue: number | string | undefined;
  centerLabel: string;
  loading?: boolean;
  emptyText?: string;
  colSpan?: number;
  rowSpan?: number;
  colStart?: number;
  format?: (value: number) => string;
};

const formatCenter = (
  value: number | string | undefined,
  format?: (value: number) => string,
) => {
  if (value === undefined) return "—";
  if (typeof value === "number")
    return format ? format(value) : value.toLocaleString();
  return value;
};

export const DonutCard = ({
  title,
  slices,
  centerValue,
  centerLabel,
  loading,
  emptyText,
  colSpan = 4,
  rowSpan = 2,
  colStart,
  format,
}: DonutCardProps) => {
  const { t } = useTranslation();
  const hasData = slices.length > 0;
  const total = slices.reduce((acc, slice) => acc + slice.value, 0);

  const size = rowSpan >= 3 ? 188 : 150;
  const outerRadius = Math.round(size * 0.48);
  const innerRadius = Math.round(size * 0.31);

  return (
    <DashboardCard
      title={title}
      colSpan={colSpan}
      rowSpan={rowSpan}
      colStart={colStart}
      sx={{ gap: "12px" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "32px",
          flexWrap: "wrap",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: `${size}px`,
            height: `${size}px`,
            flexShrink: 0,
          }}
        >
          <PieChart
            width={size}
            height={size}
            hideLegend
            series={[
              {
                innerRadius,
                outerRadius,
                paddingAngle: hasData ? 3 : 0,
                cornerRadius: 5,
                data: hasData
                  ? slices.map((slice) => ({
                      id: slice.id,
                      value: slice.value,
                      label: slice.label,
                      color: slice.color,
                    }))
                  : [{ id: "empty", value: 1, label: "", color: EMPTY_COLOR }],
              },
            ]}
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              sx={{
                fontSize: "26px",
                fontWeight: 700,
                color: "#161447",
                lineHeight: 1,
              }}
            >
              {formatCenter(loading ? undefined : centerValue, format)}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#5C5A74" }}>
              {centerLabel}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            minWidth: 0,
          }}
        >
          {hasData
            ? slices.map((slice) => {
                const share =
                  total > 0 ? Math.round((slice.value / total) * 100) : 0;
                return (
                  <Box
                    key={slice.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "3px",
                        backgroundColor: slice.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      title={slice.label}
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#161447",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "150px",
                        minWidth: 0,
                      }}
                    >
                      {slice.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#161447",
                        flexShrink: 0,
                      }}
                    >
                      {format
                        ? format(slice.value)
                        : slice.value.toLocaleString()}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "12px", color: "#5C5A74", flexShrink: 0 }}
                    >
                      ({share}%)
                    </Typography>
                  </Box>
                );
              })
            : !loading && (
                <Typography sx={{ fontSize: "14px", color: "#5C5A74" }}>
                  {emptyText ?? t("analytics.empty.data")}
                </Typography>
              )}
        </Box>
      </Box>
    </DashboardCard>
  );
};
